/**
 * A blocked run's drawer — the failure detail and its linked prepress
 * ticket, inlined (docs/features/prepress-orchestration/
 * 17_safe_to_print_gate.md §"The prepress surface" — "the ticket-inline-on-
 * blocked view is *the* reason prepress wanted this surface"). Rebuilt for
 * chunk C: it was deleted when Blocked moved to a payload with no ticket
 * (`ProblemRunResponse` carried neither the ticket nor a stage timeline);
 * the composed `GET /views/prepress/home` now carries the ticket inline, so
 * this comes back with it.
 *
 * Deliberately **no stage timeline** — prepress confirmed they do not want
 * one, and `PrepressBlockedRunOut` carries no stage-history field. Do not
 * add one here or extend the endpoint for it.
 *
 * The ticket link reuses the ticket's own `_self` field rather than
 * constructing a URL — see frontend-architecture.md's "no client-side
 * cross-resource joins".
 */
import { Link } from "react-router";
import { formatDistanceToNowStrict } from "date-fns";
import type { PrepressBlockedRun } from "@shared-core/data/api-types";
import { PREPRESS_SITE_LABELS } from "@shared-core/mock-data/prepress-home";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../primitives/sheet";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  PRIORITY_CONFIG,
  STATUS_CONFIG,
  type TicketPriority,
  type TicketStatusValue,
} from "../tickets/TicketMetadataStrip";

// The full stage vocabulary — shared with PrepressHomePage's inline row
// label so "Preflight" reads the same wherever a stage name is shown.
export const PREPRESS_STAGE_LABELS: Record<string, string> = {
  intake: "Intake",
  resolve: "Resolve",
  preflight: "Preflight",
  correction: "Correction",
  proofing: "Proofing",
  imposition: "Imposition",
  rip: "RIP",
  safe_to_print: "Safe to print",
  plate: "Plate",
  handoff: "Handoff",
};

interface PrepressBlockedDrawerProps {
  run: PrepressBlockedRun | null;
  onOpenChange: (open: boolean) => void;
}

export function PrepressBlockedDrawer({
  run,
  onOpenChange,
}: PrepressBlockedDrawerProps) {
  if (!run) return null;
  const ticket = run.ticket;
  const stageLabel = PREPRESS_STAGE_LABELS[run.stage] ?? run.stage;
  const statusLabel = run.status === "failed" ? "Failed" : "Awaiting review";

  return (
    <Sheet open={!!run} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Blocked — {run.job_reference}</SheetTitle>
          <p className="text-sm text-muted-foreground">
            {PREPRESS_SITE_LABELS[run.site]} · {run.customer_id}
          </p>
        </SheetHeader>

        <div className="px-4 space-y-6">
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Failure detail
            </h3>
            <div className="border border-border rounded-lg p-3 space-y-1.5">
              <p className="text-sm font-medium">
                {stageLabel} ·{" "}
                <span className="font-normal text-muted-foreground">
                  {statusLabel} · rev {run.revision}
                </span>
              </p>
              <p className="text-sm">{run.message ?? "—"}</p>
              <p className="text-xs text-muted-foreground">
                Recorded {formatDateTimeOrDash(run.recorded_at)}
              </p>
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Linked ticket
            </h3>
            {ticket ? (
              <TicketSummary ticket={ticket} />
            ) : (
              <p className="text-sm text-muted-foreground">
                No open prepress ticket for this job.
              </p>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TicketSummary({
  ticket,
}: {
  ticket: NonNullable<PrepressBlockedRun["ticket"]>;
}) {
  const status = ticket.status as TicketStatusValue;
  const priority = ticket.priority as TicketPriority;
  const claimed = !!ticket.assignee_user_name;
  return (
    <div className="border border-border rounded-lg p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium">{ticket.title}</p>
        <div className="flex gap-1 shrink-0">
          <Badge variant={PRIORITY_CONFIG[priority]?.variant ?? "outline"}>
            {PRIORITY_CONFIG[priority]?.label ?? ticket.priority}
          </Badge>
          <Badge variant={STATUS_CONFIG[status]?.variant ?? "outline"}>
            {STATUS_CONFIG[status]?.label ?? ticket.status}
          </Badge>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {claimed ? `claimed by ${ticket.assignee_user_name}` : "unclaimed"} ·{" "}
        {formatDistanceToNowStrict(new Date(ticket.created_at))} ago
      </p>
      <Button asChild size="sm" variant="outline">
        <Link to={ticket._self}>Open ticket</Link>
      </Button>
    </div>
  );
}
